import type firebase from "firebase/compat/app";
import type { Listing } from "../../@types/listings/listing";
import type { ListingSourceRef, ProcessedListingData, PropertyType } from "../../@types/sharedSchema";
import type { ListingState, ListingType } from "../../@types/listings/unions";

export function removeNumberFromAddress(address: string): string {
  return address?.replace(/\d+/g, "").trim();
}

export function getNumberFromAddress(address: string): number | null {
  const num = address?.replace(/\D/g, "");
  const parsed = parseInt(num);
  return isNaN(parsed) ? null : parsed;
}

/**
 * `ListingSourceRef<Timestamp>` → `ListingSourceRef<Date>` for the mirror.
 *
 * Defensive about the shape because this converter runs in TWO runtimes: the
 * browser, where `linkedAt` is a client `firebase.firestore.Timestamp`, and the
 * mirror Cloud Function, where the admin SDK hands back its own Timestamp class.
 * Both expose `.toDate()`; a plain `Date` (or a legacy row that stored one)
 * passes through untouched.
 */
const toProcessedSourceRef = (ref: ListingSourceRef<firebase.firestore.Timestamp>): ListingSourceRef<Date> => {
  const linkedAt = ref.linkedAt as unknown;
  return {
    ...ref,
    linkedAt:
      linkedAt instanceof Date
        ? linkedAt
        : typeof (linkedAt as { toDate?: () => Date })?.toDate === "function"
        ? (linkedAt as { toDate: () => Date }).toDate()
        : new Date(0)
  };
};

/**
 * ⚠ `listingContacts` IS DELIBERATELY NOT MAPPED HERE. DO NOT ADD IT BACK.
 *
 * This converter's output is written wholesale — `set()`, non-merge — into
 * `yad2-listings-latest` by the mirror Cloud Function, and that collection is
 * `allow get: if true` in `firestore.rules`: UNAUTHENTICATED. The share-link
 * favourites doc hands anonymous buyers the ids of listings a realtor chose to
 * share with them, and the public client page then point-reads exactly these
 * documents (`fetchListingsByIds`). So mirroring the recorded owner would put
 * the property owner's private mobile into a payload our own customer publishes
 * to her buyers — letting them call the seller directly and cut her out. That is
 * commercial harm to the customer, not a privacy nit.
 *
 * It briefly WAS mapped here (that is what this note replaces), and the same
 * output is additionally copied onto every referencing calendar event's
 * `listingsData`, so the leak had two mouths.
 *
 * The owner is still shown everywhere it was going to be shown — read from the
 * AUTHORITY instead, `real-estate-listings`, whose document id is the same id:
 * `utils/listings/recordedListingContacts.ts` (+ `hooks/useRecordedListingContacts`).
 * That read is also strictly fresher than the mirror, which lags by up to about a
 * minute.
 */

export function convertListingToProcessedListingData(listing: Listing): ProcessedListingData {
  const {
    id,
    owners,
    visibility,
    createdByOrganization,
    address,
    streetNumber,
    apartmentNumber,
    rooms,
    price,
    sizeOfPropertyM2,
    floor,
    floorOutOf,
    description,
    pictures,
    videoUrl,
    listingState,
    city,
    neighborhood,
    hasAirConditioning,
    hasBars,
    hasElevator,
    hasGarden,
    hasParking,
    hasStorage,
    hasTerrace,
    housingUnit,
    protectedSpace,
    hasMamak,
    hasMiklat,
    sizeOfPlotM2,
    sizeOfGardenM2,
    sizeOfTerraceM2,
    parkingSpotsCount,
    createdTime,
    updatedTime,
    listingType,
    accessibleForDisabled,
    evictionDate,
    locationDetails,
    status,
    transactionType,
    addressDisplayMode,
    evictionType,
    addressCoordinates,
    purposeType,
    madlanBulletinId,
    madlanConfirmedAddress,
    madlanLocationResolution,
    madlanPublishedByWebotUserMail,
    sourceListings,
    sourceListingIds
  } = listing;

  const parsedListingType = listingTypeLabel[listingType] ?? null;
  const parsedListingState = listingStateLabel[listingState] ?? null;

  const parsedAddressCoordinates = addressCoordinates ?? null;

  const parsedTransactionType = transactionType ?? null;
  const parsedAddressDisplayMode = addressDisplayMode ?? null;

  // Note - the way we handle listing visibility is by simply not publishing the organization key for listings that are set to "private". This way the owner can still see the listing, but it will not be visible to other users in the organization.
  const originalListingOrganizationId = visibility === "private" ? null : createdByOrganization;

  const processedListingData: ProcessedListingData = {
    listingListedFor: parsedTransactionType,
    originalListingId: id,
    transactionType: parsedTransactionType,
    addressCoordinates: parsedAddressCoordinates,
    addressDisplayMode: parsedAddressDisplayMode,
    originalListingOwnersId: owners,
    originalListingVisibility: visibility,
    originalListingOrganizationId,
    locationDetails: locationDetails,
    listingPublishedTime: createdTime?.toDate(),
    listingUpdatedTime: updatedTime?.toDate(),
    adType: "commercial",
    listingOrigin: "webot",
    originalListingUrl: "",
    propertyType: parsedListingType as PropertyType,
    address: {
      city: city,
      neighborhood: neighborhood,
      street: removeNumberFromAddress(address),
      floor: floor,
      // House number is stored in its own `streetNumber` field on the listing;
      // `address` only holds the street name. Fall back to digits embedded in
      // the street text for legacy listings that predate the split field.
      houseNumber: streetNumber ?? getNumberFromAddress(address),
      apartmentNumber: apartmentNumber ?? undefined
    },
    buildingTotalFloors: floorOutOf,
    roomsCount: rooms,
    price,
    squareMeter: sizeOfPropertyM2,
    description,
    coverImageUrl: pictures?.length > 0 ? pictures[0]?.src : null,
    imageUrls: pictures?.map((p) => p.src) ?? [],
    videoUrl: videoUrl ?? null,
    isAssetExclusive: true,
    hasAirConditioning: hasAirConditioning,
    hasBars: hasBars,
    hasElevator: hasElevator,
    hasGarden: hasGarden,
    hasParking: hasParking,
    hasStorage: hasStorage,
    hasTerrace: hasTerrace,
    hasHousingUnit: housingUnit,
    hasProtectedSpace: protectedSpace,
    // ממ"ק / מקלט: this is the ONLY way they reach search and matching, since a managed
    // listing is seen there through this mirror row. Passed through as they are, with no
    // `?? false`: a listing saved before the two fields existed has neither, and that
    // must stay "not stated" on the mirror rather than become a claimed "no".
    hasMamak: hasMamak,
    hasMiklat: hasMiklat,
    hasHandicappedAccess: accessibleForDisabled,
    sizeOfPlotM2: sizeOfPlotM2,
    sizeOfGardenM2: sizeOfGardenM2,
    sizeOfTerraceM2: sizeOfTerraceM2,
    parkingSpotsCount,
    entranceType: evictionType,
    entranceDate: evictionDate?.toDate(),
    propertyCondition: parsedListingState,
    listingStatus: status,
    isRenovated: listingState === "renovated",
    purposeType: purposeType ?? null,
    madlanBulletinId: madlanBulletinId ?? null,
    madlanConfirmedAddress: madlanConfirmedAddress ?? null,
    madlanLocationResolution: madlanLocationResolution ?? null,
    madlanPublishedByWebotUserMail: madlanPublishedByWebotUserMail ?? null,
    // Carried so a CLAIMED copy can say "מקור: יד2" and link the ad it came from
    // on the board, which renders mirror rows and never sees the source document.
    //
    // Omitted entirely when absent rather than written as `[]`: this converter's
    // output is `set()` non-merge onto the mirror, and an empty array is a value
    // every reader would then have to distinguish from "not a claimed copy".
    //
    // `linkedAt` is converted the same way the sibling date fields above are —
    // `RealEstateListing<Timestamp>` on this side, `<Date>` on the mirror. That
    // split is precisely what makes the compiler catch a missed conversion here.
    ...(sourceListings?.length ? { sourceListings: sourceListings.map(toProcessedSourceRef) } : {}),
    ...(sourceListingIds?.length ? { sourceListingIds } : {})
    // NO `listingContacts` — see the ⚠ block immediately above this function.
    // The mirror is world-readable; the owner is read from
    // `real-estate-listings` at display time instead.
  };

  return processedListingData;
}

export const listingTypeLabel: Record<ListingType, string> = {
  lot: "מגרשים",
  halls: "אולמות",
  hotel: "בית מלון",
  studio: "סטודיו",
  duplex: "דופלקס",
  clinics: "מרפאה",
  offices: "משרדים",
  triplex: "טריפלקס",
  basement: "מרתף/ פרטר",
  pentHouse: "גג/ פנטהאוז",
  apartment: "דירה",
  warehouses: "מחסנים",
  parkingLot: "חניה",
  semiDetached: "דו משפחתי",
  privateHouse: "בית פרטי/ קוטג'",
  // Byte-identical to the marketplace `propertyTypes` taxonomy (note the space
  // after the slash) so the mirrored listing lands on the same search facet.
  farm: "משק חקלאי/ נחלה",
  auxiliaryFarm: "משק עזר",
  officeBuilding: "בניין משרדים",
  gardenApartment: "דירת גן",
  sharedWorkspace: "חלל עבודה משותף",
  industrialBuildings: "מבני תעשייה",
  residentialBuilding: "בניין מגורים",
  storesCommercialSpace: "חנויות/ שטח מסחרי"
};

export const listingStateLabel: Record<ListingState, string> = {
  likeNew: "כמו חדש",
  renovated: "משופץ",
  maintained: "שמור",
  newConstruction: "פרויקט חדש - יד 1",
  needsRenovation: "דורש שיפוץ"
};
